// src/components/settings/MetaPlatformsSetup.jsx
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';

export default function MetaPlatformsSetup({ onComplete }) {
    const [step, setStep] = useState('token');
    const [accessToken, setAccessToken] = useState('');
    const [selectedPages, setSelectedPages] = useState({ facebook: null, instagram: null });
    const [availablePages, setAvailablePages] = useState([]);
    const [showInstagramGuide, setShowInstagramGuide] = useState(false);

    const InstagramConnectionGuide = ({ isOpen, onClose }) => {
        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-60">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold">Connect Instagram to Facebook</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-900 mb-2">Prerequisites</h4>
                            <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                                <li>Admin access to a Facebook Page</li>
                                <li>Instagram account converted to Business/Creator</li>
                                <li>Instagram mobile app installed</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Step 1: Convert to Business Account</h4>
                            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2 ml-4">
                                <li>Open Instagram app → Go to your profile</li>
                                <li>Tap menu (≡) → Settings → Account</li>
                                <li>Tap "Switch to Professional Account"</li>
                                <li>Choose "Business" (or "Creator")</li>
                                <li>Complete the setup flow</li>
                            </ol>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Step 2: Connect to Facebook Page</h4>
                            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2 ml-4">
                                <li>In Instagram: Settings → Account → Linked Accounts</li>
                                <li>Tap "Facebook" → "Connect to Facebook"</li>
                                <li>Log in to Facebook if prompted</li>
                                <li>Select the Facebook Page to connect</li>
                                <li>Confirm the connection</li>
                            </ol>
                        </div>

                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Step 3: Verify in Facebook</h4>
                            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2 ml-4">
                                <li>Go to your Facebook Page</li>
                                <li>Click Settings → Instagram</li>
                                <li>Verify your Instagram account is listed</li>
                            </ol>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm text-green-800">
                                <strong>Tip:</strong> After connecting, wait 2-3 minutes for the changes to sync, then click "Refresh" in the setup wizard.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Mutation to get Facebook pages
    const getPagesMutation = useMutation(
        (token) => settingsAPI.getFacebookPages(token),
        {
            onSuccess: (response) => {
                setAvailablePages(response.data.pages);
                setStep('select-pages');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to fetch pages');
            }
        }
    );

    // Mutation to complete setup
    const setupMutation = useMutation(
        (data) => settingsAPI.setupMetaPlatforms(data),
        {
            onSuccess: (response) => {
                toast.success('Meta platforms configured successfully!');
                onComplete?.();
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Setup failed');
            }
        }
    );


    const handleTokenSubmit = () => {
        if (!accessToken.trim()) {
            toast.error('Please enter an access token');
            return;
        }
        getPagesMutation.mutate(accessToken);
    };

    const handleSetupComplete = () => {
        setupMutation.mutate({
            accessToken,
            setupFacebook: !!selectedPages.facebook,
            setupInstagram: !!selectedPages.instagram,
            facebookPageId: selectedPages.facebook,
            instagramAccountId: selectedPages.instagram
        });
    };

    // Update the Quick Setup Guide section in MetaPlatformsSetup.jsx
    const TokenFlowDiagram = () => (
        <div className="bg-gray-50 rounded-lg p-4 my-4">
            <h4 className="font-medium text-gray-900 mb-3 text-center">How the Token Works</h4>
            <div className="flex items-center justify-center space-x-4 text-sm">
                <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">🔑</span>
                    </div>
                    <p className="font-medium">Your Token</p>
                </div>
                <div className="text-gray-400">→</div>
                <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">📘</span>
                    </div>
                    <p className="font-medium">Facebook Pages</p>
                </div>
                <div className="text-gray-400">→</div>
                <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">📸</span>
                    </div>
                    <p className="font-medium">Instagram Accounts</p>
                    <p className="text-xs text-gray-500 mt-1">(connected to pages)</p>
                </div>
            </div>
        </div>
    );


    const DetailedSetupGuide = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-3">📋 Prerequisites</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                    <li>A Facebook account</li>
                    <li>Admin access to at least one Facebook Page</li>
                    <li>Instagram account converted to Business/Creator type</li>
                    <li>Instagram connected to your Facebook Page</li>
                    <li>A Meta Developer account (free)</li>
                </ul>
            </div>

            <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Step 1: Create a Meta App</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ml-4">
                    <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">developers.facebook.com</a></li>
                    <li>Click "My Apps" → "Create App"</li>
                    <li>Fill in app details:
                        <ul className="list-disc list-inside ml-4 mt-1">
                            <li>App name: "PostMgr App" (or your choice)</li>
                            <li>App contact email: Your email</li>
                        </ul>
                    </li>
                    <li>Choose Filter by "Others" → "Other" as the app type</li>
                    <li>Choose "Business"</li>
                    <li>If you have a Business portfolio, link in the Details</li>
                    <li>Click "Create App"</li>
                </ol>
            </div>

            <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Step 2: Configure Instagram Business (if linking to Instagram)</h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-yellow-800">
                        <strong>Important:</strong> Your Instagram must be a Business/Creator account AND connected to a Facebook Page
                    </p>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ml-4">
                    <li>On Instagram app: Profile → Settings → Account → Switch to Professional Account</li>
                    <li>Choose "Business" (Creator also works)</li>
                    <li>Connect to your Facebook Page when prompted</li>
                    <li>Complete the category selection</li>
                </ol>
            </div>

            <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Step 3: Add Required Products and Roles</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ml-4">
                    <li>In your app dashboard, click "Add Product"</li>
                    <li>Find "Facebook Login for Business" and click "Set Up"</li>
                    <li>Go back to "Add Product" (note this is located below App Review)</li>
                    <li>If linking to Instagram follow next steps, else skip to Step 4</li>
                    <li>Find "Instagram Basic Display" and click "Set Up"</li>
                    <li>Go to "App roles" → "Roles"</li>
                    <li>Add People → Instagram Tester → Instagram Username (Business Account)</li>
                    <li>Click → "Apps and Websites" link on the Roles page to authorize app</li>
                    <li>On Apps and Websites page → Tester Invites and click "accept"</li>
                </ol>
            </div>

            <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Step 4: Generate the Access Token</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ml-4">
                    <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Graph API Explorer</a></li>
                    <li>In "Meta App" dropdown, select your app (not "Graph API Explorer")</li>
                    <li>Click "Generate Access Token"</li>
                    <li>In the permissions popup, add these permissions:
                        <div className="mt-2 bg-gray-100 rounded p-3">
                            <p className="text-xs font-mono">
                                pages_show_list<br/>
                                pages_read_engagement<br/>
                                pages_read_user_content<br/>
                                pages_manage_posts<br/>
                                instagram_basic<br/>
                                instagram_content_publish<br/>
                                instagram_manage_comments<br/>
                                instagram_manage_insights<br/>
                                business_management
                            </p>
                        </div>
                    </li>
                    <li>Click "Generate Access Token"</li>
                    <li>You may need to log in and approve permissions</li>
                </ol>
            </div>

            <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Step 5: Convert to Long-Lived Token</h4>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <p className="text-sm text-orange-800">
                        <strong>Critical:</strong> The token from Graph API Explorer expires in 1 hour. You need a long-lived token!
                    </p>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ml-4">
                    <li>Copy the short-lived token from Graph API Explorer</li>
                    <li>Go to <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Access Token Debugger</a></li>
                    <li>Paste your token and click "Debug"</li>
                    <li>At the bottom, click "Extend Access Token"</li>
                    <li>Copy the new long-lived token (expires in ~60 days)</li>
                </ol>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">✅ Token Ready!</h4>
                <p className="text-sm text-green-800">
                    Your long-lived token will work for both Facebook Pages and Instagram Business accounts
                    that are connected to those pages. Paste it below to continue.
                </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">🔧 Troubleshooting</h4>
                <div className="space-y-2 text-sm text-gray-600">
                    <details className="cursor-pointer">
                        <summary className="font-medium">No Instagram accounts showing up?</summary>
                        <ul className="list-disc list-inside mt-2 ml-4">
                            <li>Verify Instagram is set to Business/Creator account</li>
                            <li>Check Instagram is connected to a Facebook Page</li>
                            <li>Ensure you're admin of the Facebook Page</li>
                            <li>Try disconnecting and reconnecting Instagram to Facebook</li>
                        </ul>
                    </details>
                    <details className="cursor-pointer">
                        <summary className="font-medium">Token expired quickly?</summary>
                        <p className="mt-2 ml-4">
                            Make sure you extended the token using the Access Token Debugger.
                            Short-lived tokens expire in 1 hour.
                        </p>
                    </details>
                    <details className="cursor-pointer">
                        <summary className="font-medium">Permission errors?</summary>
                        <p className="mt-2 ml-4">
                            Your app may need approval for some permissions. For testing,
                            make sure you're using an account that's an admin/developer of the app.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    );

// Then in your main component, replace the simple guide with:
    if (step === 'token') {
        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Connect Meta Platforms</h3>
                    <p className="mt-1 text-sm text-gray-600">
                        Follow this detailed guide to get a token that works for both Facebook and Instagram
                    </p>
                </div>

                <DetailedSetupGuide />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Long-Lived Access Token
                    </label>
                    <textarea
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        placeholder="Paste your long-lived access token here..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                        This token should start with "EAA..." and be quite long (200+ characters)
                    </p>
                </div>

                <button
                    onClick={handleTokenSubmit}
                    disabled={getPagesMutation.isLoading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {getPagesMutation.isLoading ? 'Validating...' : 'Continue'}
                </button>

                <div className="text-center">
                    <a
                        href="https://www.youtube.com/results?search_query=facebook+graph+api+token+tutorial"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-500"
                    >
                        📹 Watch video tutorials
                    </a>
                </div>
            </div>
        );
    }

    if (step === 'select-pages') {
        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Select Your Pages</h3>
                    <p className="mt-1 text-sm text-gray-600">
                        Choose which Facebook page and Instagram account to connect
                    </p>
                </div>

                {/* Facebook Pages */}
                <div>
                    <h4 className="font-medium text-gray-700 mb-3">Facebook Pages</h4>
                    <div className="space-y-2">
                        {availablePages.map((page) => (
                            <label key={page.id} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="radio"
                                    name="facebook-page"
                                    value={page.id}
                                    checked={selectedPages.facebook === page.id}
                                    onChange={() => setSelectedPages(prev => ({ ...prev, facebook: page.id }))}
                                    className="mr-3"
                                />
                                <div>
                                    <div className="font-medium">{page.name}</div>
                                    <div className="text-sm text-gray-500">ID: {page.id}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Instagram Accounts */}
                <div>
                    <h4 className="font-medium text-gray-700 mb-3">Instagram Business Accounts</h4>
                    <div className="space-y-2">
                        {availablePages.filter(p => p.hasInstagram).map((page) => (
                            <label key={page.instagramId} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="radio"
                                    name="instagram-account"
                                    value={page.instagramId}
                                    checked={selectedPages.instagram === page.instagramId}
                                    onChange={() => setSelectedPages(prev => ({ ...prev, instagram: page.instagramId }))}
                                    className="mr-3"
                                />
                                <div>
                                    <div className="font-medium">{page.name} (Instagram)</div>
                                    <div className="text-sm text-gray-500">Connected to: {page.name}</div>
                                </div>
                            </label>
                        ))}
                        {availablePages.filter(p => p.hasInstagram).length === 0 && (
                            <button
                                onClick={() => setShowInstagramGuide(true)}
                                className="mt-2 text-sm text-blue-600 hover:text-blue-500"
                            >
                                📚 Step-by-step guide
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setStep('token')}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Back
                    </button>
                    <button
                        onClick={handleSetupComplete}
                        disabled={!selectedPages.facebook && !selectedPages.instagram}
                        className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                        Complete Setup
                    </button>
                </div>

                <InstagramConnectionGuide
                    isOpen={showInstagramGuide}
                    onClose={() => setShowInstagramGuide(false)}
                />
            </div>
        );
    }

    return null;
}